import React from 'react';
import {
  Box,
  Typography,
  Select,
  Option,
  Alert,
  CircularProgress
} from '@mui/joy';
import { useJobs } from '../../hooks/useJobs';
import type { Job } from '../../types';

interface JobSelectionProps {
  selectedJobId?: number;
  onJobSelect: (job: Job | null) => void;
  optional?: boolean;
}

export const JobSelection: React.FC<JobSelectionProps> = ({
  selectedJobId,
  onJobSelect,
  optional = true
}) => {
  const { jobs, loading } = useJobs();
  const isLoading = loading.isLoading;
  const error = loading.error;

  const handleJobChange = (
    _event: React.SyntheticEvent | null,
    newValue: string | null
  ) => {
    if (newValue && jobs) {
      const selectedJob = jobs.find(job => job.id === parseInt(newValue));
      onJobSelect(selectedJob || null);
    } else {
      onJobSelect(null);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
          Associated Job {optional && '(Optional)'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm">Loading jobs...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
          Associated Job {optional && '(Optional)'}
        </Typography>
        <Alert color="warning">
          Error loading jobs. Please refresh the page.
        </Alert>
      </Box>
    );
  }

  const jobsList = jobs || [];

  return (
    <Box>
      <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
        Associated Job
      </Typography>
      <Select
        placeholder="Select a job for this measurement..."
        value={selectedJobId?.toString() || null}
        onChange={handleJobChange}
        sx={{ width: '100%' }}
      >
        {jobsList.map((job) => (
          <Option key={job.id} value={job.id.toString()}>
            <Box>
              <Typography level="body-sm">
                {job.claim_number || `Job #${job.id}`}
              </Typography>
              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                {job.address_1}, {job.city}, {job.state} {job.zip_code}
              </Typography>
            </Box>
          </Option>
        ))}
      </Select>
      
      {jobsList.length === 0 && (
        <Alert color="neutral" sx={{ mt: 1 }}>
          No jobs available. {optional ? 'You can create a measurement without a job or create a job first if you want to link it.' : 'Create a job first before adding measurements.'}
        </Alert>
      )}
    </Box>
  );
};